import { defineComponent as _defineComponent } from 'vue'
import Client from '@/models/Client';
import ClientApi from '@/modules/client/services/ClientApi';
import {
  Agreement,
  AgreementType,
  IAgreementItemViewModel,
  getAgreementByType,
} from '@/modules/client/views/client-agreements/Agreements';
import {getAgreementViews} from '@/modules/client/views/client-agreements/AgreementsUtils';
import {clone} from '@/services/utils/BasicUtils';
import MultiformApi from '@/modules/multiForm/services/MultiformApi';
import {ProductType} from '@/commons/enums/ProductType';
import { cloneDeep } from 'lodash-es';
import ClientOwnerInfoCard from '@/modules/client/views/ClientOwnerInfoCard.vue';
import AuthService from '@/modules/user/AuthService';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';
import {ComponentPublicInstance, computed, onMounted, ref, watch} from 'vue';
import {useTour} from '@/composables/useTour';
import {useInstance} from '@/composables/useInstance';
import EnvironmentService from '@/env/EnvironmentService';
import ValidationApi from '@/commons/services/ValidationApi';


export default /*#__PURE__*/_defineComponent({
  __name: 'EditClientForm',
  props: {
    value: null,
    delegateForm: { type: Boolean },
    delegationGoal: null,
    isEdit: { type: Boolean, default: false },
    peselRequired: { type: Boolean, default: false },
    dirty: { type: Boolean, default: false },
    fieldsApiValidation: null
  },
  emits: ["update:fieldsApiValidation", "update:dirty", "update:agreement", "update:fieldCheck", "closeModal"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: 'update:fieldsApiValidation', event: any): void,
  (e: 'update:dirty', event: any): void,
  (e: 'update:agreement', event: any): void,
  (e: 'update:agreement'): void,
  (e: 'update:fieldCheck', event: any): void,
  (e: 'closeModal', event: any): void,
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: Client, delegateForm?: boolean, delegationGoal?: ProductType, isEdit: boolean, peselRequired: boolean, dirty: boolean, fieldsApiValidation?: Record<string, string | null | boolean> };



const { $refs, $errorHandler, $user, $snackbarService, $env, } = useInstance();
const keysChanged = ref<AgreementType[]>([]);
const gpdrAgreement = EnvironmentService.Environment.getGPDRDomainAgreementsWithUrl();
const clientAgreements = ref<Agreement[]>(props.delegationGoal ? [] : cloneDeep(props.value.agreements));
const clientCopy = ref<Nullable<any>>(null);
const existingClient = ref<Nullable<Client>>(null);
const checkAll = ref<Nullable<boolean>>(null);
const fieldCheck = ref<boolean>(false);



const fp_phone = ref(null)
const phoneErrorMessage = ref<String>('');
let phoneExistenceCheck: Promise<unknown> | null = null;
let phoneValidationCheck: Promise<void> | null = null;
let phoneValidatedAndExistenceCheck: Promise<void> | null = null;

const trackPhoneExistenceCheck = (check: Promise<unknown>) => {
  phoneExistenceCheck = check;
  check.finally(() => {
    if (phoneExistenceCheck === check) {
      phoneExistenceCheck = null;
    }
  });
  return check;
};

const trackPhoneValidationCheck = (check: Promise<void>) => {
  phoneValidationCheck = check;
  check.finally(() => {
    if (phoneValidationCheck === check) {
      phoneValidationCheck = null;
    }
  });
  return check;
};

const runValidatePhone = () => trackPhoneValidationCheck(validatePhone());

function resetPhoneValidation(): void {
  phoneErrorMessage.value = '';
  if (props.fieldsApiValidation?.phone) {
    props.fieldsApiValidation.phone = null;
    emit('update:fieldsApiValidation', props.fieldsApiValidation);
  }
}

async function ensurePhoneValidatedAndExistenceChecked(): Promise<void> {
  if (phoneValidatedAndExistenceCheck) {
    await phoneValidatedAndExistenceCheck;
    return;
  }

  const check = (async () => {
    if (phoneValidationCheck) {
      await phoneValidationCheck;
    } else {
      await runValidatePhone();
    }
    if (phoneErrorMessage.value) {
      return;
    }
    if (!(fp_phone.value as any)?.validate?.()) {
      return;
    }
    if (phoneExistenceCheck) {
      await phoneExistenceCheck;
      return;
    }
    await trackPhoneExistenceCheck(findIfParamExists('phone', props.value.phone));
  })();

  phoneValidatedAndExistenceCheck = check;
  try {
    await check;
  } finally {
    if (phoneValidatedAndExistenceCheck === check) {
      phoneValidatedAndExistenceCheck = null;
    }
  }
}

const onPhoneChange = () => ensurePhoneValidatedAndExistenceChecked();

async function awaitPhoneExistenceCheck(): Promise<void> {
  await ensurePhoneValidatedAndExistenceChecked();
}

const isCurrentFieldValue = (key: keyof Client, value: Nullable<string>): boolean =>
  props.value[key] === value;

async function validatePhone(): Promise<void> {
  const isValid = (fp_phone.value as any).validate();

  if (!isValid) {
    return;
  }

  const phoneAtRequest = props.value.phone as string;
  try {
    const isRealNumber = await ValidationApi.phone(phoneAtRequest);
    if (props.value.phone !== phoneAtRequest) {
      return;
    }
    phoneErrorMessage.value = '';
    if (!isRealNumber) {
      $snackbarService.openErrorSnackbar(I18NGetter().fpInternationalNumber.PHONE_CUSTOM_RULES_MSG);
      phoneErrorMessage.value = I18NGetter().fpInternationalNumber.PHONE_CUSTOM_RULES_MSG;
    }
  } catch (e) {
    if (props.value.phone !== phoneAtRequest) {
      return;
    }
    $errorHandler(e, I18NGetter().fpInternationalNumber.PHONE_CUSTOM_RULES_MSG);
    phoneErrorMessage.value = I18NGetter().fpInternationalNumber.PHONE_CUSTOM_RULES_MSG;
  }
}

const findIfParamExists = async(key: keyof Client, value: Nullable<string>) => {
  fieldCheck.value = false;
  emit('update:fieldCheck', fieldCheck.value);
  if (value && (clientCopy.value === null || clientCopy.value[key] !== value)) {
    if (!isCurrentFieldValue(key, value)) {
      return;
    }
    const field = ($refs[`fp_${key}`] as any);
    if (props.fieldsApiValidation && isCurrentFieldValue(key, value)) {
      props.fieldsApiValidation[key] = null;
    }
    const validField: boolean = field.valid || field?.validate();
    if (value.length && validField) {
      if (!isCurrentFieldValue(key, value)) {
        return;
      }
      const properties: Record<string, string | boolean> = {};
      properties[key] = value;
      const validationKeys = ['email', 'phone',];
      if (validationKeys.includes(key)) {
        properties.isIndividual = !props.value.isCompany;
      }
      try {
        const client = await ClientApi.clientsSearch(properties);
        if (!isCurrentFieldValue(key, value)) {
          return;
        }
        existingClient.value = client;
        if (existingClient.value?.id && props.fieldsApiValidation) {
          props.fieldsApiValidation[key] = String(existingClient.value?.id);
          emit('update:fieldsApiValidation', props.fieldsApiValidation);
        }
      } catch (e) {
        if (!isCurrentFieldValue(key, value)) {
          return false;
        }
        $errorHandler(e);
        return false;
      }
    }
  }
};

const validatePhoneNumberAndEmail = () => {
  findIfParamExists('email', props.value.email);
  findIfParamExists('phone', props.value.phone);
};

watch(() => props.value.isCompany, validatePhoneNumberAndEmail);

const agreements = computed<IAgreementItemViewModel[]>(() => {
  let agreementsList = getAgreementViews().filter(agreement => agreement.key !== 'AcceptsBigPersonalDataProcessing');
  if (!props.delegationGoal && !$user.value.isHomeEstateUser) {
    agreementsList = agreementsList.filter(x => !x.isRealEstate);
  }
  if ($user.value.isHomeEstateUser && !props.delegationGoal) {
    agreementsList = agreementsList.filter(x => x.productTypes.includes(ProductType.REAL_ESTATE));
  }
  if (props.delegationGoal) {
    agreementsList = agreementsList.filter(x => x.productTypes.includes(props.delegationGoal!));
  }

  agreementsList = agreementsList.filter(x => !x.isCompany);
  return agreementsList;
});

const requiredAgreements = computed<IAgreementItemViewModel[]>(() => {
  return agreements.value.filter(agreement => {
    return agreement.required(props.value.agreements) &&
      (!agreement.organisationIds || agreement.organisationIds.includes($user.value.organisationId!));
  });
});

const additionalAgreements = computed<IAgreementItemViewModel[]>(() => {
  return agreements.value.filter(agreement => {
    return !agreement.required(props.value.agreements) &&
      (!agreement.organisationIds || agreement.organisationIds.includes($user.value.organisationId!));
  });
});

const fieldCheckChanged = () => {
  fieldCheck.value = true;
  emit('update:fieldCheck', fieldCheck.value);
  fieldChanged();
}

const fieldChanged = () => {
  emit('update:dirty', fieldCheck.value);
};

const showClientOwnerInfoCard = computed<boolean>(() => {
  return !!existingClient.value &&
        !!existingClient.value.owner &&
        existingClient.value.owner?.id !== AuthService.User?.id
});

const updateKeysChanged = (key: AgreementType) => {
  const existedChangedKeyId = keysChanged.value.indexOf(key);
  if (existedChangedKeyId > -1) {
    keysChanged.value.splice(existedChangedKeyId, 1);
  } else {
    keysChanged.value.push(key);
  }
}

const agreementChanged = (key: AgreementType) => {
  fieldChanged();
  updateKeysChanged(key);
  emit('update:agreement', {keysChanged: keysChanged.value, agreements: clientAgreements.value,});
};
const {$onboarding,} = useInstance()
const clientInfoRef = ref<ComponentPublicInstance | null>(null)
const fillAgreementsRef = ref<ComponentPublicInstance | null>(null)

onMounted(async() => {
  if (await $onboarding.tours.LEAD.isEnabled()) {
    useTour(clientInfoRef.value!, $onboarding.tours.LEAD.step.CLIENT_INFO);
    useTour(fillAgreementsRef.value!, $onboarding.tours.LEAD.step.FILL_AGREEMENTS)
  }

  props.value.mainAddress.landId = $user.value.landId;
  props.value.mainAddress.districtId = $user.value.districtId;
  if (props.isEdit) {
    clientCopy.value = clone(props.value);
  }
});

const findRegon = async(nip: Nullable<string>) => {
  if (nip?.length === 10) {
    try {
      const response = await MultiformApi.getCompanyDataByNip(nip);
      if (response) {
        props.value.regon = response.regon;
      }
    } catch (e) {
      $snackbarService.openWarningSnackbar(I18NGetter().useEditClientForm.SNACKBAR_WARNING_REGON_NIP);
    }
  }
  fieldChanged();
};

const onCheckAllChange = () => {
  fieldChanged();
  agreementsChange(requiredAgreements.value, props.isEdit);
  agreementsChange(additionalAgreements.value);
  emit('update:agreement', {keysChanged: keysChanged.value, agreements: clientAgreements.value,});
};

const agreementsChange = (agreements: Array<IAgreementItemViewModel>, canSkipIfChecked: boolean = false) => {
  agreements.forEach(agreement => {
    if (canSkipIfChecked && getAgreementByType(clientAgreements.value, agreement.key).value) {
      return;
    }

    getAgreementByType(clientAgreements.value, agreement.key).value = checkAll.value!;
    updateKeysChanged(agreement.key);
  });
};

const showLink = computed(() => {
  // for domain == LENDI_PL show link
  // for NESTO_RO show only users with admin / verifier / coordinator permissions
  return ($env.isAppDomain().NESTO_RO && ($user.value.isAdminOrVerifier || $user.value.isCoordinator)) || $env.isAppDomain().LENDI_PL;
});

expose({
  awaitPhoneExistenceCheck,
});

return { __sfc: true,props, $refs, $errorHandler, $user, $snackbarService, $env, keysChanged, gpdrAgreement, clientAgreements, clientCopy, existingClient, checkAll, fieldCheck, emit, fp_phone, phoneErrorMessage, phoneExistenceCheck, phoneValidationCheck, phoneValidatedAndExistenceCheck, trackPhoneExistenceCheck, trackPhoneValidationCheck, runValidatePhone, resetPhoneValidation, ensurePhoneValidatedAndExistenceChecked, onPhoneChange, awaitPhoneExistenceCheck, isCurrentFieldValue, validatePhone, findIfParamExists, validatePhoneNumberAndEmail, agreements, requiredAgreements, additionalAgreements, fieldCheckChanged, fieldChanged, showClientOwnerInfoCard, updateKeysChanged, agreementChanged, $onboarding, clientInfoRef, fillAgreementsRef, findRegon, onCheckAllChange, agreementsChange, showLink, getAgreementByType, ClientOwnerInfoCard, I18NGetter }
}

})